import { createDevAgentSessionViewState, useDevAgentStore } from "@/stores/devAgentStore";
import {
  createDevAgentSession,
  listDevAgentSessions,
  refreshSseTicket,
} from "../devAgentService";
import { pickPreferredDevAgentSession } from "./sessionPersistence";
import { mapDevAgentSessionsFromApi, type DevAgentChatSessionRow } from "./types";

export async function fetchDevAgentSessions(
  userId: string,
  orgId?: string,
): Promise<DevAgentChatSessionRow[]> {
  const sessions = await listDevAgentSessions(userId, orgId);
  return mapDevAgentSessionsFromApi(sessions);
}

export async function loadInitialDevAgentSessions(params: {
  userId: string;
  orgId?: string;
  currentSessionUuid: string | null;
}): Promise<{
  sessions: DevAgentChatSessionRow[];
  selectedSessionUuid: string | null;
  sseTicket: string | null;
}> {
  const sessions = await fetchDevAgentSessions(params.userId, params.orgId);
  if (params.currentSessionUuid || sessions.length > 0) {
    const preferred = params.currentSessionUuid
      ? sessions.find((session) => session.uuid === params.currentSessionUuid) || null
      : pickPreferredDevAgentSession(sessions, params.userId, params.orgId);
    return {
      sessions,
      selectedSessionUuid: preferred?.uuid ?? null,
      sseTicket: preferred?.sseTicket ?? null,
    };
  }

  const created = await createDevAgentSession(params.userId, params.orgId);
  return {
    sessions: [
      {
        uuid: created.uuid,
        title: created.title || "OrgCompass",
        sseTicket: (created as any).sse_ticket ?? null,
      },
    ],
    selectedSessionUuid: created.uuid,
    sseTicket: (created as any).sse_ticket ?? null,
  };
}

export async function resolveDevAgentSessionTicket(params: {
  sessionUuid: string;
  sessions: DevAgentChatSessionRow[];
  userId?: string;
  orgId?: string;
  setSessions?: (sessions: DevAgentChatSessionRow[]) => void;
}): Promise<string | null> {
  try {
    const refreshed = await refreshSseTicket(params.sessionUuid);
    if (refreshed) return refreshed;
  } catch {
    /* fall through */
  }

  const fromCurrentSessions =
    params.sessions.find((session) => session.uuid === params.sessionUuid)?.sseTicket ?? null;
  if (fromCurrentSessions != null) return fromCurrentSessions;

  if (!params.userId) return null;
  try {
    const reloaded = await fetchDevAgentSessions(params.userId, params.orgId);
    params.setSessions?.(reloaded);
    return reloaded.find((session) => session.uuid === params.sessionUuid)?.sseTicket ?? null;
  } catch {
    return null;
  }
}

export function applyDevAgentSessionSwitch(
  sessionUuid: string,
  sseTicket: string | null,
) {
  const prevUuid = useDevAgentStore.getState().sessionUuid;
  const base = createDevAgentSessionViewState(sessionUuid, sseTicket);
  // Improve-with-AI injects `intent_confirm` before async `loadInitialDevAgentSessions`
  // resolves and calls this. Without preserving it, the feed resets to [] and
  // the Review & Send card (and its table) vanish. Only drop when switching to
  // a *different* session (prev set and changed).
  const keepIntentConfirm =
    prevUuid == null || prevUuid === sessionUuid;
  const preservedIntent = keepIntentConfirm
    ? useDevAgentStore.getState().feedItems.filter((f) => f.type === "intent_confirm")
    : [];
  useDevAgentStore.setState({
    ...base,
    feedItems: preservedIntent,
  });
}
